import type { CSSProperties } from 'react';
import type { StainDto } from '../api/companions';
import { STAIN_TONES, stainPath } from './stainPath';

const overlayStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
};

/**
 * Permanent battle stains, drawn over a pet (spec 2026-08-23 §5.4).
 *
 * ONE component serves all three of this app's pet-render paths -- the 2D pet under `Pet3DStage`, the
 * direct `<img>` in `BattleFighter`, and the profile figure (a 3D snapshot).
 * That works because a stain's x/y are normalised into the sprite's own square box, which every
 * one of those paths shares. It also preserves the "never a live Compositor" perf rule the
 * social surfaces are built on (§11.5): this is one SVG, not a layer stack.
 *
 * The caller's wrapping element MUST be `position: relative` (or otherwise positioned) — this
 * overlay is `position: absolute; inset: 0` and will otherwise escape to the nearest positioned
 * ancestor, landing somewhere wrong. `Compositor`'s own container already is; `BattleFighter`'s
 * `.battle-fighter-facing` is given it in
 * base.css specifically to host this.
 *
 * Purely decorative, so `aria-hidden` -- the companion's own label already names it, and a
 * screen reader announcing "three blood stains" would be noise, not information.
 */
export function StainOverlay({
  stains,
  className,
  opacity = 1,
  maskUrl,
}: {
  stains: StainDto[];
  className?: string;
  /** Task 16 (2026-08-23 battle-gore-and-wash): lets a caller fade the overlay as a wash
   *  proceeds (`stainFadeFor` in `habitat/washStages.ts`) without touching the underlying
   *  `stains` record, which only changes once, at the end of the wash. Defaults to 1 (fully
   *  visible) so every pre-existing caller keeps rendering exactly as before. */
  opacity?: number;
  /** Task 5 (2026-09-06 thrown-blood-splash, §4.5): the pet's OWN sprite URL, used as a CSS
   *  mask so stains clip to the silhouette instead of spilling onto the transparent margin of
   *  the square box. Deliberately the pet's own art, never a separate mask asset -- a
   *  mask-image that fails to load hides the element completely rather than degrading to
   *  "unmasked", so a standalone mask file could silently erase every stain in the game if it
   *  ever went missing. If the pet's own sprite is missing there is no pet left to stain either,
   *  so the failure modes coincide. Omit (or pass null) to render exactly as before this task —
   *  unmasked. */
  maskUrl?: string | null;
}) {
  // spec B3: a stain not visible from the front has no 2D spot to show on.
  const shown = stains.filter((s) => s.anchor?.front !== false);
  if (!shown.length) return null;
  const style: CSSProperties = {
    ...overlayStyle,
    ...(opacity !== 1 ? { opacity } : null),
    // Object-literal key (not a bracket-assignment) on purpose: `scripts/verify-tokens.mjs`
    // recognises a quoted-key-followed-by-colon as a real custom-property definition, the same
    // idiom `HabitatScene.tsx` already uses for their own inline `--*` vars.
    ...(maskUrl ? ({ '--stain-mask': `url("${maskUrl}")` } as CSSProperties) : null),
  };
  return (
    <svg
      className={className ? `stain-overlay ${className}` : 'stain-overlay'}
      style={style}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      {shown.map((s) => (
        <g
          key={s.seed}
          transform={`translate(${s.x * 100} ${s.y * 100}) rotate(${s.rot}) scale(${s.scale}) translate(-50 -50)`}
        >
          <path d={stainPath(s.seed)} fill={STAIN_TONES[s.tone] ?? STAIN_TONES[1]} />
        </g>
      ))}
    </svg>
  );
}
